import { Box, Grid, PageWrapper, Typography } from '@oresundsbron/bridge-ui';
import { cx } from 'class-variance-authority';
import Image from 'next/image';
import { FC, useMemo } from 'react';
import * as AspectRatio from '@radix-ui/react-aspect-ratio';
import { Carousel } from '../../components/Carousel';
import { SEOMetadata } from '../../components/SEOMetadata';
import { useClubGuide } from '../../hooks/useClubGuide';
import { pipe } from 'fp-ts/lib/function';
import {
  alt,
  chain,
  fromNullable,
  fromPredicate,
  map,
  toUndefined,
} from 'fp-ts/lib/Option';
import { map as mapA, head } from 'fp-ts/lib/Array';
import { RichText } from '../../components/RichText';
import { Breadcrumbs } from '../Breadcrumbs';

const col = cx('col-span-full', 'md:col-start-3', 'md:col-span-8');

export const GuideArticle: FC<{ id: string }> = ({ id }) => {
  const { data, seoMetadata } = useClubGuide(id);

  const images = useMemo(
    () =>
      pipe(
        data,
        fromNullable,
        chain((data) => pipe(data.images, fromPredicate(Array.isArray))),
        chain((imgs) =>
          pipe(
            imgs,
            fromPredicate((x) => x.length > 1),
            map(
              mapA((img) => (
                <Image
                  key={img.sys.id}
                  alt={img.description || ''}
                  src={img.url}
                  sizes="(max-width: 768px) 100vw,
              (max-width: 1200px) 50vw,
              33vw"
                  fill
                  style={{
                    aspectRatio: '16/9',
                    objectFit: 'cover',
                  }}
                />
              ))
            ),
            map((imgs) => <Carousel className={cx(col)}>{imgs}</Carousel>),
            alt(() =>
              pipe(
                imgs,
                head,
                map((img) => (
                  <Box className={cx(col)}>
                    <AspectRatio.Root ratio={16 / 9}>
                      <Image
                        key={img.sys.id}
                        alt={img.description || ''}
                        src={img.url}
                        sizes="(max-width: 768px) 100vw,
              (max-width: 1200px) 50vw,
              33vw"
                        fill
                        style={{
                          aspectRatio: '16/9',
                          objectFit: 'cover',
                        }}
                      />
                    </AspectRatio.Root>
                  </Box>
                ))
              )
            )
          )
        ),
        toUndefined
      ),
    [data]
  );

  if (!data) {
    return null;
  }

  return (
    <>
      <SEOMetadata items={seoMetadata} customJsonLd={data?.jsonLd} />
      <PageWrapper data-mode="club">
        <Grid className="col-start-2 my-8 gap-y-8 md:my-24 md:gap-y-12">
          <Breadcrumbs className="col-span-full" id={id} />
          <Typography className={cx(col, 'flex', 'gap-2')}>
            {data.category ? (
              <Typography
                as="span"
                className="uppercase text-purple-600"
                intent="title"
                size="sm"
              >
                {data.category.externalName}
              </Typography>
            ) : null}
            {data.location ? (
              <Typography
                as="span"
                className="text-neutral-500"
                intent="label"
                weight="medium"
              >
                {data.location.externalName}
              </Typography>
            ) : null}
          </Typography>
          <Box className={cx(col)}>
            {data.title ? (
              <Typography as="h1" intent="display" className="mb-2 md:mb-3">
                {data.title}
              </Typography>
            ) : null}
            {data.subtitle ? (
              <Typography intent="label" size="lg">
                {data.subtitle}
              </Typography>
            ) : null}
          </Box>
          {images}
          {data.preamble ? (
            <Typography size="lg" className={cx(col)}>
              {data.preamble}
            </Typography>
          ) : null}
          {data.content ? (
            <Box className={col}>
              <RichText content={data.content} />
            </Box>
          ) : null}
        </Grid>
      </PageWrapper>
    </>
  );
};
